/**
 * Feature auto-registration factory utility
 *
 * This factory eliminates boilerplate by providing a simple way to create
 * self-registering feature modules with consistent error handling and logging.
 */

import { registerFeatureModule } from './registry';
import { getStore } from './storeReference';
import { FeatureModule } from './types';

/**
 * Configuration options for the feature factory
 */
export interface FeatureFactoryOptions {
  /**
   * Whether to log registration attempts (default: true)
   */
  enableLogging?: boolean;

  /**
   * Custom delay before registration in milliseconds (default: 0)
   * Useful for handling dependencies or ensuring store is ready
   */
  registrationDelay?: number;

  /**
   * Whether to suppress error logging (default: false)
   * Set to true if you want to handle errors manually
   */
  suppressErrors?: boolean;
}

/**
 * Default factory options
 */
const DEFAULT_OPTIONS: Required<FeatureFactoryOptions> = {
  enableLogging: true,
  registrationDelay: 0,
  suppressErrors: false,
};

/**
 * Creates a self-registering feature module
 *
 * This factory handles all the boilerplate around auto-registration:
 * - Automatic store registration with error handling
 * - Consistent logging
 * - Configurable timing for dependencies
 * - Clean export pattern
 *
 * @param featureModule The feature module to auto-register
 * @param options Optional configuration for registration behavior
 * @returns An object with the feature and a manual register function
 *
 * @example
 * ```typescript
 * // In your feature's index.ts:
 * import { createAutoRegisteringFeature } from '../../store/features/factory';
 * import { myFeature } from './store/feature';
 *
 * // Auto-register with defaults
 * const { feature } = createAutoRegisteringFeature(myFeature);
 *
 * // Export everything else...
 * export * from './store';
 * ```
 */
export function createAutoRegisteringFeature(
  featureModule: FeatureModule,
  options: FeatureFactoryOptions = {},
) {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  /**
   * Performs the actual registration with error handling
   */
  const performRegistration = () => {
    if (opts.enableLogging) {
      console.log(`🔧 Auto-registering ${featureModule.name} feature...`);
    }

    try {
      const store = getStore();
      const result = registerFeatureModule(featureModule, store);

      if (!result.success) {
        const errorMsg = `Failed to auto-register ${featureModule.name} feature: ${result.error}`;
        if (!opts.suppressErrors) {
          console.error(errorMsg);
        }
        return { success: false, error: errorMsg };
      }

      if (opts.enableLogging) {
        console.log(`✅ Successfully auto-registered ${featureModule.name} feature`);
      }

      return { success: true };
    } catch (error) {
      const errorMsg = `Error during ${featureModule.name} feature auto-registration: ${error}`;
      if (!opts.suppressErrors) {
        console.error(errorMsg);
      }
      return { success: false, error: errorMsg };
    }
  };

  /**
   * Schedule the registration based on delay option
   */
  if (opts.registrationDelay > 0) {
    setTimeout(performRegistration, opts.registrationDelay);
  } else {
    // Use setTimeout(0) to allow other modules to load first (handles dependencies)
    setTimeout(performRegistration, 0);
  }

  return {
    /**
     * The feature module (for re-export if needed)
     */
    feature: featureModule,

    /**
     * Manual registration function (in case you need to register again or handle registration manually)
     */
    register: performRegistration,
  };
}

/**
 * Convenience function for the most common use case - simple auto-registration
 *
 * @param featureModule The feature module to auto-register
 * @returns The feature module (for easier re-export)
 *
 * @example
 * ```typescript
 * // In your feature's index.ts:
 * import { autoRegisterFeature } from '../../store/features/factory';
 * import { myFeature } from './store/feature';
 *
 * // One-liner auto-registration
 * autoRegisterFeature(myFeature);
 *
 * // Export everything...
 * export * from './store';
 * ```
 */
export function autoRegisterFeature(featureModule: FeatureModule): FeatureModule {
  createAutoRegisteringFeature(featureModule);
  return featureModule;
}

/**
 * Advanced factory for features with dependencies
 *
 * This ensures the feature is registered after its dependencies are available.
 * Useful for complex feature hierarchies.
 *
 * @param featureModule The feature module to auto-register
 * @param options Registration options with dependency handling
 */
export function createDependentFeature(
  featureModule: FeatureModule,
  options: FeatureFactoryOptions & {
    /**
     * Additional delay to wait for dependencies (default: 100ms)
     */
    dependencyDelay?: number;
  } = {},
) {
  const dependencyDelay = options.dependencyDelay ?? 100;

  return createAutoRegisteringFeature(featureModule, {
    ...options,
    registrationDelay: dependencyDelay,
    enableLogging: options.enableLogging ?? true,
  });
}
